/**
 * Hook to fetch stories from content-api.
 * Uses the /stories/ endpoint which returns full scene data including audioId.
 * Falls back to hardcoded STORIES on error.
 */

import { useState, useEffect, useRef } from 'react'
import { useI18n } from '@/i18n'
import { fetchStories, fetchStory } from '@/api/content'
import { FALLBACK_STORIES } from '@/data/stories'
import type { Story, StoryScene } from '@/data/stories'
import type { StoryDetail } from '@/api/content'

interface UseStoriesResult {
  stories: Story[]
  loading: boolean
  error: string | null
}

export function mapStoryDetail(d: StoryDetail): Story {
  return {
    id: d.story_group,
    title: d.title,
    subtitle: d.subtitle,
    narrator: (d.narrator || 'tschauko') as 'tschauko' | 'tschauka',
    cover: d.cover || '',
    color: d.color || '#7CB342',
    region: d.region || '',
    duration: d.duration || '',
    scenes: d.scenes.map(s => ({
      id: s.id,
      title: s.title,
      narrative: s.narrative,
      mood: (s.mood || 'mystisch') as StoryScene['mood'],
      illustrationPrompt: s.illustrationPrompt || '',
      illustrationId: s.illustrationId ?? undefined,
      videoId: s.videoId ?? undefined,
      audioId: s.audioId ?? undefined,
      location: s.location || undefined,
    })),
    availableLanguages: d.available_languages || [],
    language: d.language,
    postId: d.id,
  }
}

export function useStories(): UseStoriesResult {
  const { lang } = useI18n()
  const [stories, setStories] = useState<Story[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)

      try {
        // 1. Fetch story list
        const list = await fetchStories(lang)
        if (cancelled) return

        // 2. Fetch full story detail for each (with scenes + audioId)
        const details = await Promise.all(
          list.stories.map(item => fetchStory(item.story_group, lang))
        )
        if (cancelled) return

        // 3. Map to Story interface
        setStories(details.map(mapStoryDetail))
        setLoading(false)
      } catch (err: any) {
        if (cancelled) return
        console.warn('Stories API failed, using fallback:', err.message)
        setError(err.message)
        setStories(FALLBACK_STORIES)
        setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [lang])

  return { stories, loading, error }
}

/**
 * useStory — EINE Geschichte anhand ihrer `story_group` laden.
 *
 * Warum nicht `useStories()`: Das laedt die Liste UND anschliessend das
 * Detail JEDER Geschichte. In der Entdecken-Liste wuerde das bei jedem
 * geoeffneten Ort passieren, auch bei einer Bushaltestelle, die gar keine
 * Geschichte hat. Hier wird nur geholt, was gebraucht wird — und modulweit
 * gemerkt, damit derselbe Ort beim zweiten Antippen sofort da ist.
 */
const storyCache = new Map<string, Story>()

export function useStory(storyGroup: string | null | undefined): { story: Story | null; loading: boolean } {
  const { lang } = useI18n()
  const schluessel = storyGroup ? `${storyGroup}:${lang}` : null
  const [story, setStory] = useState<Story | null>(() => (schluessel ? storyCache.get(schluessel) ?? null : null))
  const [loading, setLoading] = useState(Boolean(schluessel) && !story)

  useEffect(() => {
    if (!schluessel || !storyGroup) { setStory(null); setLoading(false); return }
    const gemerkt = storyCache.get(schluessel)
    if (gemerkt) { setStory(gemerkt); setLoading(false); return }
    let lebt = true
    setLoading(true)
    fetchStory(storyGroup, lang)
      .then(d => {
        const s = mapStoryDetail(d)
        storyCache.set(schluessel, s)
        if (!lebt) return
        setStory(s)
        setLoading(false)
      })
      .catch(() => {
        // Ohne Geschichte bleibt die gewoehnliche Detailansicht stehen —
        // besser als eine leere Buehne.
        if (!lebt) return
        setStory(null)
        setLoading(false)
      })
    return () => { lebt = false }
  }, [schluessel, storyGroup, lang])

  return { story, loading }
}
